import React, { useRef, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { theme, neutral } from '../../design-system/theme';

// Dynamic import for Infographic to handle SSR
let InfographicClass: any = null;

interface InfographicViewerProps {
  syntax: string;
  height?: number | string;
  width?: number | string;
  editable?: boolean;
  className?: string;
}

const OuterContainer = styled.div<{ $height?: number | string }>`
  width: 100%;
  height: ${props => typeof props.$height === 'number' ? `${props.$height}px` : props.$height || '400px'};
  border-radius: 12px;
  overflow: hidden;
  background: ${neutral[1]};
  position: relative;
`;

const LoadingPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${theme.textSecondary};
  font-size: 14px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  background: ${neutral[1]};
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${theme.danger};
  font-size: 14px;
  padding: 20px;
  text-align: center;
`;

/**
 * InfographicViewer component for rendering AntV Infographic visualizations
 * Uses a separate inner container to prevent React DOM conflicts
 */
export const InfographicViewer: React.FC<InfographicViewerProps> = ({
  syntax,
  height = 400,
  width = '100%',
  editable = false,
  className,
}) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerContainerRef = useRef<HTMLDivElement | null>(null);
  const infographicRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const cleanupInfographic = useCallback(() => {
    if (infographicRef.current) {
      try {
        infographicRef.current.destroy?.();
      } catch (e) {
        // Ignore cleanup errors
      }
      infographicRef.current = null;
    }
    
    // Manually clear the inner container
    if (innerContainerRef.current) {
      while (innerContainerRef.current.firstChild) {
        innerContainerRef.current.removeChild(innerContainerRef.current.firstChild);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    
    const loadInfographic = async () => {
      if (!outerRef.current || !mountedRef.current) return;
      
      try {
        // Create a fresh inner container for each render
        cleanupInfographic();
        
        // Create new inner div
        const innerDiv = document.createElement('div');
        innerDiv.style.width = '100%';
        innerDiv.style.height = '100%';
        outerRef.current.appendChild(innerDiv);
        innerContainerRef.current = innerDiv;

        // Load the Infographic class if not loaded
        if (!InfographicClass) {
          const module = await import('@antv/infographic');
          InfographicClass = module.Infographic;
        }

        if (!mountedRef.current) return;

        // Create new instance
        infographicRef.current = new InfographicClass({
          container: innerDiv,
          width: typeof width === 'number' ? width : '100%',
          height: typeof height === 'number' ? height : '100%',
          editable,
        });

        await infographicRef.current.render(syntax);
        
        if (mountedRef.current) {
          setLoading(false);
          setError(null);
        }
      } catch (err) {
        console.error('Failed to render infographic:', err);
        if (mountedRef.current) {
          setError('Failed to render infographic. Please try again.');
          setLoading(false);
        }
      }
    };

    setLoading(true);
    loadInfographic();

    return () => {
      mountedRef.current = false;
      cleanupInfographic();
      innerContainerRef.current = null;
    };
  }, [syntax, height, width, editable, cleanupInfographic]);

  return (
    <OuterContainer ref={outerRef} $height={height} className={className}>
      {loading && <LoadingPlaceholder>Loading infographic...</LoadingPlaceholder>}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </OuterContainer>
  );
};

export default InfographicViewer;
